/**
 * Theme.js - Main JavaScript for Shopify Hardware Shop Theme
 * Vanilla ES6+ | No dependencies
 */

/* ==========================================================================
   Utility Functions
   ========================================================================== */

const Utils = {
  /**
   * Format cents into a money string using Shopify format.
   * @param {number} cents
   * @param {string} format - e.g. '${{amount}}', '${{amount_with_comma_separator}}'
   * @returns {string}
   */
  formatMoney(cents, format = '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = parseFloat(cents) / 100;
    const placeholders = {
      amount: value.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,'),
      amount_no_decimals: Math.round(value).toLocaleString('en-US'),
      amount_with_comma_separator: value.toFixed(2).replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.'),
      amount_no_decimals_with_comma_separator: Math.round(value).toLocaleString('de-DE'),
    };
    let result = format;
    for (const [key, val] of Object.entries(placeholders)) {
      result = result.replace(`{{${key}}}`, val);
    }
    return result;
  },

  debounce(fn, delay = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  },

  trapFocus(container) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    container.addEventListener('keydown', handler);
    container._trapFocusHandler = handler;
    first.focus();
  },

  removeTrapFocus(container) {
    if (container._trapFocusHandler) {
      container.removeEventListener('keydown', container._trapFocusHandler);
      delete container._trapFocusHandler;
    }
  },

  fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/${type}`,
      },
    };
  },

  lockBodyScroll() {
    document.body.style.overflow = 'hidden';
    document.body.dataset.scrollLocked = 'true';
  },

  unlockBodyScroll() {
    document.body.style.overflow = '';
    delete document.body.dataset.scrollLocked;
  },
};

/* ==========================================================================
   Mobile Menu
   ========================================================================== */

class MobileMenu {
  constructor() {
    this.drawer = document.querySelector('[data-mobile-menu]');
    this.toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
    if (!this.drawer || !this.toggleBtn) return;

    this.closeBtn = this.drawer.querySelector('[data-mobile-menu-close]');
    this.overlay = this.drawer.querySelector('[data-mobile-menu-overlay]');
    this.isOpen = false;
    this.bindEvents();
  }

  bindEvents() {
    this.toggleBtn.addEventListener('click', () => this.toggle());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Sub-menu navigation via event delegation
    this.drawer.addEventListener('click', (e) => {
      const subMenuTrigger = e.target.closest('[data-submenu-trigger]');
      if (subMenuTrigger) {
        e.preventDefault();
        const targetId = subMenuTrigger.dataset.submenuTrigger;
        const submenu = this.drawer.querySelector(`[data-submenu="${targetId}"]`);
        if (submenu) submenu.classList.add('is-active');
      }

      const backBtn = e.target.closest('[data-submenu-back]');
      if (backBtn) {
        const submenu = backBtn.closest('[data-submenu]');
        if (submenu) submenu.classList.remove('is-active');
      }
    });
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.drawer.classList.add('is-open');
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    Utils.lockBodyScroll();
    Utils.trapFocus(this.drawer);
  }

  close() {
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.toggleBtn.setAttribute('aria-expanded', 'false');
    Utils.unlockBodyScroll();
    Utils.removeTrapFocus(this.drawer);
    // Reset sub-menus
    this.drawer.querySelectorAll('[data-submenu].is-active').forEach((el) => el.classList.remove('is-active'));
    this.toggleBtn.focus();
  }
}

/* ==========================================================================
   Cart Drawer
   ========================================================================== */

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.overlay = this.drawer.querySelector('[data-cart-drawer-overlay]');
    this.closeBtn = this.drawer.querySelector('[data-cart-drawer-close]');
    this.itemsContainer = this.drawer.querySelector('[data-cart-items]');
    this.subtotalEl = this.drawer.querySelector('[data-cart-subtotal]');
    this.countBadges = document.querySelectorAll('[data-cart-count]');
    this.emptyMsg = this.drawer.querySelector('[data-cart-empty]');
    this.isOpen = false;
    this.moneyFormat = this.drawer.dataset.moneyFormat || '${{amount}}';

    this.bindEvents();
  }

  bindEvents() {
    // Open triggers
    document.addEventListener('click', (e) => {
      const openTrigger = e.target.closest('[data-cart-drawer-open]');
      if (openTrigger) { e.preventDefault(); this.open(); }
    });

    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Quantity & remove via delegation
    if (this.itemsContainer) {
      this.itemsContainer.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-cart-qty-minus]');
        const plus = e.target.closest('[data-cart-qty-plus]');
        const remove = e.target.closest('[data-cart-remove]');

        if (minus) {
          const key = minus.dataset.cartQtyMinus;
          const input = this.itemsContainer.querySelector(`[data-cart-qty-input="${key}"]`);
          const qty = Math.max(0, parseInt(input?.value || '1', 10) - 1);
          this.changeItem(key, qty);
        }
        if (plus) {
          const key = plus.dataset.cartQtyPlus;
          const input = this.itemsContainer.querySelector(`[data-cart-qty-input="${key}"]`);
          const qty = parseInt(input?.value || '1', 10) + 1;
          this.changeItem(key, qty);
        }
        if (remove) {
          this.changeItem(remove.dataset.cartRemove, 0);
        }
      });
    }
  }

  open() {
    this.isOpen = true;
    this.drawer.classList.add('is-open');
    Utils.lockBodyScroll();
    Utils.trapFocus(this.drawer);
    this.fetchAndRender();
  }

  close() {
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    Utils.unlockBodyScroll();
    Utils.removeTrapFocus(this.drawer);
  }

  async addToCart(formData) {
    try {
      const res = await fetch('/cart/add.js', {
        ...Utils.fetchConfig(),
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error(await res.text());
      await this.fetchAndRender();
      this.open();
      return true;
    } catch (err) {
      console.error('Add to cart error:', err);
      return false;
    }
  }

  async changeItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        ...Utils.fetchConfig(),
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error(await res.text());
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart change error:', err);
    }
  }

  async fetchAndRender() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }

  renderCart(cart) {
    // Update count badges
    this.countBadges.forEach((badge) => {
      badge.textContent = cart.item_count;
      badge.hidden = cart.item_count === 0;
    });

    // Update subtotal
    if (this.subtotalEl) {
      this.subtotalEl.textContent = Utils.formatMoney(cart.total_price, this.moneyFormat);
    }

    // Toggle empty message
    if (this.emptyMsg) this.emptyMsg.hidden = cart.item_count > 0;

    // Render items
    if (!this.itemsContainer) return;
    if (cart.items.length === 0) {
      this.itemsContainer.innerHTML = '';
      return;
    }

    this.itemsContainer.innerHTML = cart.items
      .map(
        (item) => `
      <div class="cart-drawer__item" data-cart-item="${item.key}">
        <div class="cart-drawer__item-image">
          <img src="${item.featured_image?.url || item.image}" alt="${item.title}" loading="lazy" width="80" height="80">
        </div>
        <div class="cart-drawer__item-details">
          <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
          ${item.variant_title ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
          <p class="cart-drawer__item-price">${Utils.formatMoney(item.final_line_price, this.moneyFormat)}</p>
          <div class="cart-drawer__item-qty">
            <button type="button" data-cart-qty-minus="${item.key}" aria-label="Decrease quantity">−</button>
            <input type="number" value="${item.quantity}" min="0" readonly data-cart-qty-input="${item.key}" aria-label="Quantity">
            <button type="button" data-cart-qty-plus="${item.key}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button type="button" class="cart-drawer__item-remove" data-cart-remove="${item.key}" aria-label="Remove ${item.title}">×</button>
      </div>`
      )
      .join('');
  }
}

/* ==========================================================================
   Product Page
   ========================================================================== */

class ProductPage {
  constructor() {
    this.section = document.querySelector('[data-product-section]');
    if (!this.section) return;

    this.mainImage = this.section.querySelector('[data-product-main-image]');
    this.thumbnails = this.section.querySelector('[data-product-thumbnails]');
    this.variantSelect = this.section.querySelector('[data-variant-select]');
    this.optionInputs = this.section.querySelectorAll('[data-option-input]');
    this.priceEl = this.section.querySelector('[data-product-price]');
    this.comparePriceEl = this.section.querySelector('[data-product-compare-price]');
    this.addToCartBtn = this.section.querySelector('[data-add-to-cart]');
    this.addToCartText = this.section.querySelector('[data-add-to-cart-text]');
    this.productForm = this.section.querySelector('[data-product-form]');
    this.moneyFormat = this.section.dataset.moneyFormat || '${{amount}}';

    try {
      this.productData = JSON.parse(this.section.querySelector('[data-product-json]')?.textContent || '{}');
    } catch {
      this.productData = {};
    }

    this.bindEvents();
    this.initGallerySwipe();
  }

  bindEvents() {
    // Thumbnails
    if (this.thumbnails) {
      this.thumbnails.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumbnail]');
        if (!thumb) return;
        e.preventDefault();
        this.changeMainImage(thumb.dataset.thumbnail, thumb.dataset.thumbnailAlt || '');
        this.thumbnails.querySelectorAll('[data-thumbnail]').forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    }

    // Variant selection (single select or combined options)
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', () => this.onVariantChange());
    }
    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.onOptionChange());
    });

    // Quantity +/-
    this.section.addEventListener('click', (e) => {
      const qtyMinus = e.target.closest('[data-qty-minus]');
      const qtyPlus = e.target.closest('[data-qty-plus]');
      const input = this.section.querySelector('[data-qty-input]');
      if (!input) return;
      const current = parseInt(input.value, 10) || 1;
      if (qtyMinus) input.value = Math.max(1, current - 1);
      if (qtyPlus) input.value = current + 1;
    });

    // Add to cart
    if (this.productForm) {
      this.productForm.addEventListener('submit', (e) => this.handleAddToCart(e));
    }

    // Accordion tabs
    this.section.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-accordion-trigger]');
      if (!trigger) return;
      const content = trigger.nextElementSibling;
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!isOpen));
      if (content) content.hidden = isOpen;
    });
  }

  changeMainImage(src, alt) {
    if (!this.mainImage) return;
    this.mainImage.src = src;
    if (alt) this.mainImage.alt = alt;
  }

  onVariantChange() {
    const variantId = this.variantSelect.value;
    const variant = this.productData.variants?.find((v) => String(v.id) === variantId);
    if (variant) this.updateForVariant(variant);
  }

  onOptionChange() {
    const selectedOptions = Array.from(this.optionInputs)
      .filter((el) => el.checked || el.tagName === 'SELECT')
      .map((el) => el.value);

    const variant = this.productData.variants?.find((v) =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
    if (variant) {
      if (this.variantSelect) this.variantSelect.value = variant.id;
      this.updateForVariant(variant);
    }
  }

  updateForVariant(variant) {
    // Price
    if (this.priceEl) this.priceEl.textContent = Utils.formatMoney(variant.price, this.moneyFormat);
    if (this.comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = Utils.formatMoney(variant.compare_at_price, this.moneyFormat);
        this.comparePriceEl.hidden = false;
      } else {
        this.comparePriceEl.hidden = true;
      }
    }

    // Availability
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !variant.available;
    }
    if (this.addToCartText) {
      this.addToCartText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }

    // URL
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // Image
    if (variant.featured_image?.src) {
      this.changeMainImage(variant.featured_image.src, variant.featured_image.alt || '');
    }
  }

  async handleAddToCart(e) {
    e.preventDefault();
    if (!this.addToCartBtn || this.addToCartBtn.disabled) return;

    const variantId = this.variantSelect?.value || this.productForm.querySelector('[name="id"]')?.value;
    const quantity = parseInt(this.section.querySelector('[data-qty-input]')?.value || '1', 10);
    if (!variantId) return;

    this.addToCartBtn.classList.add('is-loading');
    this.addToCartBtn.disabled = true;

    const cartDrawer = window.theme?.cartDrawer;
    const success = cartDrawer
      ? await cartDrawer.addToCart({ id: parseInt(variantId, 10), quantity })
      : false;

    this.addToCartBtn.classList.remove('is-loading');
    this.addToCartBtn.disabled = false;

    if (success && this.addToCartText) {
      const original = this.addToCartText.textContent;
      this.addToCartText.textContent = 'Added!';
      setTimeout(() => { this.addToCartText.textContent = original; }, 2000);
    }
  }

  initGallerySwipe() {
    const gallery = this.section.querySelector('[data-product-gallery]');
    if (!gallery) return;

    let startX = 0;
    let startY = 0;
    const THRESHOLD = 50;

    gallery.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    gallery.addEventListener('touchend', (e) => {
      const diffX = e.changedTouches[0].clientX - startX;
      const diffY = e.changedTouches[0].clientY - startY;
      if (Math.abs(diffX) < THRESHOLD || Math.abs(diffY) > Math.abs(diffX)) return;

      const thumbs = this.thumbnails?.querySelectorAll('[data-thumbnail]');
      if (!thumbs?.length) return;
      const activeIndex = Array.from(thumbs).findIndex((t) => t.classList.contains('is-active'));
      const nextIndex = diffX < 0
        ? Math.min(activeIndex + 1, thumbs.length - 1)
        : Math.max(activeIndex - 1, 0);

      thumbs[nextIndex]?.click();
    }, { passive: true });
  }
}

/* ==========================================================================
   Predictive Search
   ========================================================================== */

class PredictiveSearch {
  constructor() {
    this.input = document.querySelector('[data-search-input]');
    if (!this.input) return;

    this.results = document.querySelector('[data-search-results]');
    this.form = this.input.closest('form');
    this.selectedIndex = -1;
    this.abortController = null;

    this.input.addEventListener('input', Utils.debounce((e) => this.onInput(e), 300));
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-search-wrapper]')) this.closeResults();
    });
  }

  async onInput() {
    const query = this.input.value.trim();
    if (query.length < 2) { this.closeResults(); return; }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=4`,
        { signal: this.abortController.signal }
      );
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      this.renderResults(data.resources?.results || {});
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Search error:', err);
    }
  }

  renderResults(results) {
    if (!this.results) return;
    const sections = [];
    const groups = [
      { key: 'products', label: 'Products' },
      { key: 'collections', label: 'Collections' },
      { key: 'pages', label: 'Pages' },
      { key: 'articles', label: 'Articles' },
    ];

    for (const { key, label } of groups) {
      const items = results[key];
      if (!items?.length) continue;
      const list = items
        .map((item) => {
          const image = item.image || item.featured_image?.url || '';
          const price = item.price ? ` <span class="search-result__price">${Utils.formatMoney(parseFloat(item.price) * 100)}</span>` : '';
          return `<li class="search-result__item" role="option">
            <a href="${item.url}" class="search-result__link">
              ${image ? `<img src="${image}" alt="" width="40" height="40" loading="lazy" class="search-result__image">` : ''}
              <span class="search-result__title">${item.title}${price}</span>
            </a>
          </li>`;
        })
        .join('');
      sections.push(`<div class="search-result__group"><h3 class="search-result__heading">${label}</h3><ul role="listbox">${list}</ul></div>`);
    }

    if (sections.length === 0) {
      this.results.innerHTML = '<p class="search-result__empty">No results found</p>';
    } else {
      this.results.innerHTML = sections.join('');
    }
    this.results.hidden = false;
    this.selectedIndex = -1;
  }

  onKeydown(e) {
    if (!this.results || this.results.hidden) return;
    const items = this.results.querySelectorAll('.search-result__item');
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
      this.highlightItem(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
      this.highlightItem(items);
    } else if (e.key === 'Enter' && this.selectedIndex >= 0) {
      e.preventDefault();
      const link = items[this.selectedIndex]?.querySelector('a');
      if (link) window.location.href = link.href;
    } else if (e.key === 'Escape') {
      this.closeResults();
    }
  }

  highlightItem(items) {
    items.forEach((item, i) => {
      item.classList.toggle('is-highlighted', i === this.selectedIndex);
    });
    items[this.selectedIndex]?.scrollIntoView({ block: 'nearest' });
  }

  closeResults() {
    if (this.results) this.results.hidden = true;
    this.selectedIndex = -1;
  }
}

/* ==========================================================================
   Header Scroll Behavior
   ========================================================================== */

class StickyHeader {
  constructor() {
    this.header = document.querySelector('[data-header]');
    if (!this.header) return;

    this.threshold = parseInt(this.header.dataset.scrollThreshold, 10) || 100;
    this.lastScroll = 0;

    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.onScroll(); // initial state
  }

  onScroll() {
    const scrollY = window.scrollY;
    this.header.classList.toggle('scrolled', scrollY > this.threshold);

    // Hide/show on scroll direction
    if (scrollY > this.lastScroll && scrollY > this.threshold) {
      this.header.classList.add('header--hidden');
    } else {
      this.header.classList.remove('header--hidden');
    }
    this.lastScroll = scrollY;
  }
}

/* ==========================================================================
   Collection Page (Filters & Sort)
   ========================================================================== */

class CollectionPage {
  constructor() {
    this.section = document.querySelector('[data-collection-section]');
    if (!this.section) return;

    this.filterToggle = this.section.querySelector('[data-filter-toggle]');
    this.filterDrawer = this.section.querySelector('[data-filter-drawer]');
    this.sortSelect = this.section.querySelector('[data-sort-select]');

    this.bindEvents();
  }

  bindEvents() {
    if (this.filterToggle && this.filterDrawer) {
      this.filterToggle.addEventListener('click', () => {
        const isOpen = this.filterDrawer.classList.toggle('is-open');
        this.filterToggle.setAttribute('aria-expanded', String(isOpen));
        if (isOpen) {
          Utils.lockBodyScroll();
        } else {
          Utils.unlockBodyScroll();
        }
      });

      // Close filter on overlay or close button
      this.filterDrawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-filter-close]') || e.target.matches('[data-filter-drawer]')) {
          this.filterDrawer.classList.remove('is-open');
          this.filterToggle.setAttribute('aria-expanded', 'false');
          Utils.unlockBodyScroll();
        }
      });
    }

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => {
        const url = new URL(window.location);
        url.searchParams.set('sort_by', this.sortSelect.value);
        window.location.href = url.toString();
      });
    }
  }
}

/* ==========================================================================
   Slideshow / Hero
   ========================================================================== */

class Slideshow {
  constructor(el) {
    this.container = el;
    this.slides = el.querySelectorAll('[data-slide]');
    if (this.slides.length < 2) return;

    this.dots = el.querySelector('[data-slideshow-dots]');
    this.prevBtn = el.querySelector('[data-slideshow-prev]');
    this.nextBtn = el.querySelector('[data-slideshow-next]');
    this.current = 0;
    this.interval = parseInt(el.dataset.slideshowInterval, 10) || 5000;
    this.timer = null;

    this.init();
  }

  init() {
    this.slides.forEach((s, i) => {
      s.classList.toggle('is-active', i === 0);
      s.setAttribute('aria-hidden', i !== 0);
    });

    if (this.dots) {
      this.dots.innerHTML = Array.from(this.slides)
        .map((_, i) => `<button type="button" class="slideshow__dot${i === 0 ? ' is-active' : ''}" data-dot="${i}" aria-label="Go to slide ${i + 1}"></button>`)
        .join('');
      this.dots.addEventListener('click', (e) => {
        const dot = e.target.closest('[data-dot]');
        if (dot) this.goTo(parseInt(dot.dataset.dot, 10));
      });
    }

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

    this.container.addEventListener('mouseenter', () => this.pause());
    this.container.addEventListener('mouseleave', () => this.play());

    this.play();
  }

  goTo(index) {
    if (index === this.current) return;
    this.slides[this.current].classList.remove('is-active');
    this.slides[this.current].setAttribute('aria-hidden', 'true');
    this.current = (index + this.slides.length) % this.slides.length;
    this.slides[this.current].classList.add('is-active');
    this.slides[this.current].setAttribute('aria-hidden', 'false');

    if (this.dots) {
      this.dots.querySelectorAll('[data-dot]').forEach((d, i) => {
        d.classList.toggle('is-active', i === this.current);
      });
    }
  }

  next() { this.goTo(this.current + 1); this.resetTimer(); }
  prev() { this.goTo(this.current - 1); this.resetTimer(); }

  play() { this.timer = setInterval(() => this.next(), this.interval); }
  pause() { clearInterval(this.timer); }
  resetTimer() { this.pause(); this.play(); }
}

/* ==========================================================================
   Scroll Animations
   ========================================================================== */

class ScrollAnimations {
  constructor() {
    this.elements = document.querySelectorAll('.animate-on-scroll');
    if (!this.elements.length || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const delay = parseInt(el.dataset.animationDelay, 10) || 0;
          setTimeout(() => el.classList.add('is-visible'), delay);
          this.observer.unobserve(el);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    this.elements.forEach((el, i) => {
      // Auto-stagger grid items
      if (el.dataset.animationDelay === undefined && el.parentElement) {
        const siblings = el.parentElement.querySelectorAll('.animate-on-scroll');
        if (siblings.length > 1) {
          const idx = Array.from(siblings).indexOf(el);
          el.dataset.animationDelay = String(idx * 100);
        }
      }
      this.observer.observe(el);
    });
  }
}

/* ==========================================================================
   Newsletter Form
   ========================================================================== */

class NewsletterForm {
  constructor() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-newsletter-form]');
      if (!form) return;
      e.preventDefault();
      this.handleSubmit(form);
    });
  }

  async handleSubmit(form) {
    const msgEl = form.querySelector('[data-newsletter-message]');
    const submitBtn = form.querySelector('button[type="submit"]');
    const emailInput = form.querySelector('input[type="email"]');

    if (!emailInput?.value.trim()) {
      this.showMessage(msgEl, 'Please enter your email address.', 'error');
      return;
    }

    if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add('is-loading'); }

    try {
      const formData = new FormData(form);
      const res = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      if (res.ok || res.status === 200) {
        this.showMessage(msgEl, 'Thank you for subscribing!', 'success');
        form.reset();
      } else {
        throw new Error('Subscription failed');
      }
    } catch (err) {
      this.showMessage(msgEl, 'Something went wrong. Please try again.', 'error');
      console.error('Newsletter error:', err);
    } finally {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove('is-loading'); }
    }
  }

  showMessage(el, text, type) {
    if (!el) return;
    el.textContent = text;
    el.className = `newsletter__message newsletter__message--${type}`;
    el.hidden = false;
    setTimeout(() => { el.hidden = true; }, 5000);
  }
}

/* ==========================================================================
   Initialize
   ========================================================================== */

window.theme = window.theme || {};

document.addEventListener('DOMContentLoaded', () => {
  // Core components
  window.theme.mobileMenu = new MobileMenu();
  window.theme.cartDrawer = new CartDrawer();
  window.theme.productPage = new ProductPage();
  window.theme.predictiveSearch = new PredictiveSearch();
  window.theme.stickyHeader = new StickyHeader();
  window.theme.collectionPage = new CollectionPage();
  window.theme.newsletterForm = new NewsletterForm();
  window.theme.scrollAnimations = new ScrollAnimations();

  // Init all slideshows
  document.querySelectorAll('[data-slideshow]').forEach((el) => {
    new Slideshow(el);
  });

  // Expose utilities globally
  window.theme.utils = Utils;
});

/* ==========================================================================
   MODERN UI/UX ENHANCEMENTS
   ========================================================================== */

/* --- Enhanced Scroll Animations with IntersectionObserver --- */
(function() {
  const animateElements = document.querySelectorAll('.animate-on-scroll, .animate-slide-left, .animate-slide-right, .animate-scale');

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          // Add staggered delay for grid children
          const parent = entry.target.parentElement;
          if (parent && parent.classList.contains('grid')) {
            const siblings = Array.from(parent.children);
            const index = siblings.indexOf(entry.target);
            entry.target.style.transitionDelay = (index * 0.1) + 's';
          }
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    animateElements.forEach(el => observer.observe(el));
  } else {
    // Fallback: show everything
    animateElements.forEach(el => el.classList.add('is-visible'));
  }
})();

/* --- Back to Top Button --- */
(function() {
  const btn = document.createElement('button');
  btn.className = 'back-to-top';
  btn.setAttribute('aria-label', 'Back to top');
  btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 16V4M10 4L4 10M10 4L16 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  document.body.appendChild(btn);

  let ticking = false;
  window.addEventListener('scroll', function() {
    if (!ticking) {
      requestAnimationFrame(function() {
        if (window.scrollY > 500) {
          btn.classList.add('is-visible');
        } else {
          btn.classList.remove('is-visible');
        }
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });

  btn.addEventListener('click', function() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
})();

/* --- Animated Counter (for stats sections) --- */
(function() {
  const counters = document.querySelectorAll('[data-counter]');
  if (!counters.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const el = entry.target;
        const target = parseInt(el.getAttribute('data-counter'), 10);
        const suffix = el.getAttribute('data-suffix') || '';
        const prefix = el.getAttribute('data-prefix') || '';
        const duration = 2000;
        const start = 0;
        const startTime = performance.now();

        function update(currentTime) {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          // Ease out cubic
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = Math.round(start + (target - start) * eased);
          el.textContent = prefix + current.toLocaleString() + suffix;
          if (progress < 1) {
            requestAnimationFrame(update);
          }
        }
        requestAnimationFrame(update);
        observer.unobserve(el);
      }
    });
  }, { threshold: 0.5 });

  counters.forEach(el => observer.observe(el));
})();

/* --- 3D Tilt Effect for Cards --- */
(function() {
  if (window.matchMedia('(pointer: coarse)').matches) return; // Skip on touch devices

  const cards = document.querySelectorAll('.collection-card, .testimonial-card');

  cards.forEach(card => {
    card.addEventListener('mousemove', function(e) {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = (y - centerY) / centerY * -5;
      const rotateY = (x - centerX) / centerX * 5;

      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px)`;
    });

    card.addEventListener('mouseleave', function() {
      card.style.transform = '';
    });
  });
})();

/* --- Smooth Header Color Transition on Scroll --- */
(function() {
  const header = document.querySelector('.header');
  if (!header) return;

  let ticking = false;
  window.addEventListener('scroll', function() {
    if (!ticking) {
      requestAnimationFrame(function() {
        if (window.scrollY > 100) {
          header.classList.add('scrolled');
        } else {
          header.classList.remove('scrolled');
        }
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
})();

/* --- Product Image Zoom on Hover --- */
(function() {
  const mainImage = document.querySelector('.product__media-main');
  if (!mainImage) return;

  const img = mainImage.querySelector('.product__media-image--main, .product__media-image');
  if (!img) return;

  mainImage.addEventListener('mousemove', function(e) {
    const rect = mainImage.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    img.style.transformOrigin = x + '% ' + y + '%';
    img.style.transform = 'scale(1.8)';
  });

  mainImage.addEventListener('mouseleave', function() {
    img.style.transform = 'scale(1)';
    img.style.transformOrigin = 'center center';
  });
})();

/* --- Add to Cart Success Animation --- */
(function() {
  const cartBtns = document.querySelectorAll('[data-add-to-cart], .product__add-to-cart');

  cartBtns.forEach(btn => {
    btn.addEventListener('click', function() {
      if (btn.classList.contains('is-loading')) return;

      // Show loading state
      const originalText = btn.textContent;
      btn.classList.add('is-loading');
      btn.textContent = 'Adding...';

      // After form submission/AJAX completes, show success
      setTimeout(() => {
        btn.textContent = '\u2713 Added!';
        btn.style.background = 'linear-gradient(135deg, #22c55e, #16a34a)';

        setTimeout(() => {
          btn.textContent = originalText;
          btn.style.background = '';
          btn.classList.remove('is-loading');
        }, 1500);
      }, 800);
    });
  });
})();

/* --- Parallax Effect for Hero --- */
(function() {
  const hero = document.querySelector('.hero-slideshow');
  if (!hero || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let ticking = false;
  window.addEventListener('scroll', function() {
    if (!ticking) {
      requestAnimationFrame(function() {
        const scrolled = window.scrollY;
        const heroHeight = hero.offsetHeight;
        if (scrolled < heroHeight) {
          const rate = scrolled * 0.3;
          const images = hero.querySelectorAll('.hero-slide__image');
          images.forEach(img => {
            img.style.transform = 'translateY(' + rate + 'px) scale(1.1)';
          });
          // Fade out content as you scroll
          const contents = hero.querySelectorAll('.hero-slide__content');
          contents.forEach(content => {
            content.style.opacity = Math.max(0.3, 1 - (scrolled / heroHeight * 1.5));
          });
        }
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
})();

/* --- Typing Effect (disabled - causes content flash) --- */

/* --- Newsletter Form Enhancement --- */
(function() {
  const forms = document.querySelectorAll('.newsletter__form');
  forms.forEach(form => {
    form.addEventListener('submit', function(e) {
      const btn = form.querySelector('.newsletter__submit');
      if (btn) {
        btn.textContent = 'Subscribing...';
        btn.disabled = true;

        setTimeout(() => {
          btn.textContent = '\u2713 Subscribed!';
          btn.style.background = 'linear-gradient(135deg, #22c55e, #16a34a)';
          btn.style.color = '#fff';

          // Create confetti-like particles
          for (let j = 0; j < 12; j++) {
            const particle = document.createElement('span');
            particle.style.cssText = 'position:fixed;width:8px;height:8px;border-radius:50%;pointer-events:none;z-index:9999;';
            particle.style.background = ['#E85D26', '#F5A623', '#1B2A4A', '#22c55e'][Math.floor(Math.random() * 4)];
            particle.style.left = btn.getBoundingClientRect().left + btn.offsetWidth / 2 + 'px';
            particle.style.top = btn.getBoundingClientRect().top + 'px';
            document.body.appendChild(particle);

            const angle = (Math.PI * 2 / 12) * j;
            const velocity = 60 + Math.random() * 40;
            const dx = Math.cos(angle) * velocity;
            const dy = Math.sin(angle) * velocity - 30;

            particle.animate([
              { transform: 'translate(0, 0) scale(1)', opacity: 1 },
              { transform: `translate(${dx}px, ${dy + 80}px) scale(0)`, opacity: 0 }
            ], { duration: 800, easing: 'cubic-bezier(0, 0.9, 0.57, 1)' }).onfinish = () => particle.remove();
          }
        }, 500);
      }
    });
  });
})();

/* --- Smooth Section Transitions (disabled - was hiding content on load) --- */

/* ==========================================================================
   PRODUCT CARD - AJAX ADD TO CART & QUANTITY CONTROLS
   ========================================================================== */
(function() {
  function updateCartCount(count) {
    const badges = document.querySelectorAll('.header-amazon__cart-count, [data-cart-count]');
    badges.forEach(function(badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    });
    // If no badge exists yet, create one
    if (badges.length === 0 && count > 0) {
      const cartIcon = document.querySelector('.header-amazon__cart-icon');
      if (cartIcon) {
        const span = document.createElement('span');
        span.className = 'header-amazon__cart-count';
        span.textContent = count;
        cartIcon.appendChild(span);
      }
    }
  }

  function showQtyControls(container, qty) {
    var addBtn = container.querySelector('[data-add-to-card-btn]');
    var qtyControls = container.querySelector('[data-qty-controls]');
    var qtyCount = container.querySelector('[data-qty-count]');
    if (addBtn) addBtn.classList.add('product-card__add-btn--hidden');
    if (qtyControls) {
      qtyControls.classList.remove('product-card__qty--hidden');
      qtyControls.classList.remove('product-card__qty--loading');
    }
    if (qtyCount) qtyCount.textContent = qty;
  }

  function showAddBtn(container) {
    var addBtn = container.querySelector('[data-add-to-card-btn]');
    var qtyControls = container.querySelector('[data-qty-controls]');
    if (addBtn) {
      addBtn.classList.remove('product-card__add-btn--hidden');
      addBtn.classList.remove('product-card__add-btn--loading');
    }
    if (qtyControls) qtyControls.classList.add('product-card__qty--hidden');
  }

  // Add to Cart
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-add-to-card-btn]');
    if (!btn) return;
    e.preventDefault();

    var container = btn.closest('[data-variant-id]');
    if (!container) return;
    var variantId = container.getAttribute('data-variant-id');

    btn.classList.add('product-card__add-btn--loading');
    btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation:spin 0.6s linear infinite"><circle cx="12" cy="12" r="10" stroke-dasharray="50" stroke-dashoffset="20"/></svg> Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
    })
    .then(function(r) { return r.json(); })
    .then(function(item) {
      showQtyControls(container, item.quantity);
      // Update cart count
      fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
        updateCartCount(cart.item_count);
      });
    })
    .catch(function(err) {
      console.error('Add to cart error:', err);
      btn.classList.remove('product-card__add-btn--loading');
      btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6H20L18.5 14H7.5L5 2H2"/><circle cx="8.5" cy="19.5" r="1.5" fill="currentColor"/><circle cx="17.5" cy="19.5" r="1.5" fill="currentColor"/></svg> Add to Cart';
    });
  });

  // Increment quantity
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-qty-plus]');
    if (!btn) return;

    var container = btn.closest('[data-variant-id]');
    if (!container) return;
    var variantId = container.getAttribute('data-variant-id');
    var qtyControls = container.querySelector('[data-qty-controls]');
    var qtyCount = container.querySelector('[data-qty-count]');
    var currentQty = parseInt(qtyCount.textContent) || 0;

    qtyControls.classList.add('product-card__qty--loading');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: currentQty + 1 })
    })
    .then(function(r) { return r.json(); })
    .then(function(cart) {
      // Find the item in the updated cart
      var newQty = 0;
      cart.items.forEach(function(item) {
        if (String(item.variant_id) === String(variantId)) {
          newQty = item.quantity;
        }
      });
      if (newQty > 0) {
        showQtyControls(container, newQty);
      } else {
        showAddBtn(container);
      }
      updateCartCount(cart.item_count);
    })
    .catch(function() {
      qtyControls.classList.remove('product-card__qty--loading');
    });
  });

  // Decrement quantity
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-qty-minus]');
    if (!btn) return;

    var container = btn.closest('[data-variant-id]');
    if (!container) return;
    var variantId = container.getAttribute('data-variant-id');
    var qtyControls = container.querySelector('[data-qty-controls]');
    var qtyCount = container.querySelector('[data-qty-count]');
    var currentQty = parseInt(qtyCount.textContent) || 0;

    if (currentQty <= 1) {
      // Remove from cart - quantity 0
      qtyControls.classList.add('product-card__qty--loading');

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 })
      })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        showAddBtn(container);
        updateCartCount(cart.item_count);
      })
      .catch(function() {
        qtyControls.classList.remove('product-card__qty--loading');
      });
    } else {
      qtyControls.classList.add('product-card__qty--loading');

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: currentQty - 1 })
      })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var newQty = 0;
        cart.items.forEach(function(item) {
          if (String(item.variant_id) === String(variantId)) {
            newQty = item.quantity;
          }
        });
        if (newQty > 0) {
          showQtyControls(container, newQty);
        } else {
          showAddBtn(container);
        }
        updateCartCount(cart.item_count);
      })
      .catch(function() {
        qtyControls.classList.remove('product-card__qty--loading');
      });
    }
  });

  // Add spinner animation
  var style = document.createElement('style');
  style.textContent = '@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}';
  document.head.appendChild(style);
})();
